import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import {
  Text,
  Stack,
  Group,
  Avatar,
  Button,
  Anchor,
  Skeleton,
} from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { usersService } from '../../services/users';
import { LoginPromptModal } from '../common/LoginPromptModal';
import type { SuggestedUser } from '../../types';

export function SuggestedUsers() {
  const { user, isAuthenticated } = useAuth();
  const [followed, setFollowed] = useState<Set<string>>(new Set());
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ['suggested-users', user?.id],
    queryFn: () => usersService.getSuggested(5),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const followMutation = useMutation({
    mutationFn: (id: string) => usersService.follow(id),
    onSuccess: (_, id) => setFollowed((prev) => new Set(prev).add(id)),
  });

  const visible = suggestions.filter((s) => !followed.has(s.id));

  if (!isLoading && visible.length === 0) return null;

  const handleFollow = (id: string) => {
    if (!isAuthenticated) { setShowLoginPrompt(true); return; }
    followMutation.mutate(id);
  };

  return (
    <>
      <Stack gap="sm">
        <Group justify="space-between">
          <Text size="sm" fw={600}>Suggested for you</Text>
          <Anchor component={Link} to="/discover/people" size="xs" fw={600}>See all</Anchor>
        </Group>

        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <UserSkeleton key={i} />)
        ) : (
          visible.map((s) => (
            <SuggestedUserRow
              key={s.id}
              user={s}
              isFollowing={followed.has(s.id)}
              isPending={followMutation.isPending && followMutation.variables === s.id}
              onFollow={() => handleFollow(s.id)}
            />
          ))
        )}
      </Stack>

      <LoginPromptModal
        opened={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        action="follow reviewers"
      />
    </>
  );
}

function SuggestedUserRow({
  user,
  isFollowing,
  isPending,
  onFollow,
}: {
  user: SuggestedUser;
  isFollowing: boolean;
  isPending: boolean;
  onFollow: () => void;
}) {
  const mutualLine = user.mutualUsername
    ? user.mutualCount > 1
      ? `Followed by @${user.mutualUsername} + ${user.mutualCount - 1} more`
      : `Followed by @${user.mutualUsername}`
    : `${user.reviewCount} review${user.reviewCount !== 1 ? 's' : ''}`;

  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Anchor
        component={Link}
        to={`/users/${user.username}`}
        style={{ flex: 1, minWidth: 0, textDecoration: 'none', color: 'inherit' }}
      >
        <Group gap="sm" wrap="nowrap">
          <Avatar src={user.profileImageUrl} radius="xl" size="md">
            <IconUser size={16} />
          </Avatar>
          <Stack gap={1} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} lineClamp={1}>@{user.username}</Text>
            <Text size="xs" c="dimmed" lineClamp={1}>{mutualLine}</Text>
          </Stack>
        </Group>
      </Anchor>

      {!isFollowing ? (
        <Button
          size="xs"
          radius="xl"
          variant="outline"
          loading={isPending}
          onClick={onFollow}
          style={{ flexShrink: 0 }}
        >
          Follow
        </Button>
      ) : (
        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>Following</Text>
      )}
    </Group>
  );
}

function UserSkeleton() {
  return (
    <Group justify="space-between">
      <Group gap="sm">
        <Skeleton height={38} circle />
        <Stack gap={4}>
          <Skeleton height={12} width={90} />
          <Skeleton height={10} width={120} />
        </Stack>
      </Group>
      <Skeleton height={28} width={62} radius="xl" />
    </Group>
  );
}
